import { AnyAction, createSlice, Slice } from '@reduxjs/toolkit';
import { TAppDispatch, TState } from '~/src/store';
import { KeycloakTokenParsed } from 'keycloak-js';

interface IExtKeycloakTokenParsed extends KeycloakTokenParsed {
    given_name: string,
    email: string,
}

export interface IUserInfo {
    name: string;
    email: string;
}

export interface IAuthSlice {
    tokenInfo?: {
        token: string;
        refreshToken: string;
        tokenParsed: IExtKeycloakTokenParsed;
    };
    userInfo?: IUserInfo;
    authenticated: boolean;
}

const authSlice: Slice<IAuthSlice> = createSlice({
    name: 'auth',
    initialState: {
        authenticated: false,
    },
    reducers: {
        set: (state: IAuthSlice, action: AnyAction) => {
            const { authenticated, tokenInfo, userInfo } = action.payload;
            state.authenticated = authenticated;
            state.tokenInfo =  tokenInfo;
            state.userInfo = userInfo;
        },
    },
});

const { actions } = authSlice;

export const checkAuth = () => {
    return async (dispatch: TAppDispatch, getState: () => TState): Promise<void> => {
        const { auth: { authenticated: isAuth } } = getState();
        if (!isAuth) {
            const keycloak = (await import('~/src/services/auth')).default;
            await keycloak.init({ onLoad: 'check-sso', checkLoginIframe: false });
            if (!keycloak.authenticated) {
                await keycloak.login();
            }
            const { token, refreshToken, authenticated } = keycloak;
            const tokenParsed = keycloak.tokenParsed as IExtKeycloakTokenParsed;

            dispatch(actions.set({
                authenticated,
                tokenInfo: {
                    refreshToken,
                    tokenParsed,
                    token,
                },
                userInfo: {
                    name: tokenParsed?.given_name,
                    email: tokenParsed?.email,
                },
            }));
        }
    };
};

export const logout = () => {
    return async (dispatch: TAppDispatch): Promise<void> => {
        const keycloak = (await import('~/src/services/auth')).default;
        await keycloak.logout();

        if (!keycloak.authenticated) {
            dispatch(actions.set({
                authenticated: false,
                tokenInfo: undefined,
                userInfo: undefined,
            }));
        }
    };
};

export const selectAuth = (state: TState): IAuthSlice => state.auth;

export default authSlice.reducer;
